/**
 * Query-failure bus — the missing half of the global `QueryCache.onError`
 * seam in `app/providers.tsx`.
 *
 * WHY THIS EXISTS
 * ---------------
 * A failed `useQuery` leaves `data` undefined. Pages overwhelmingly write
 * `const rows = q.data?.data ?? []`, so a failure renders as an EMPTY LIST —
 * indistinguishable from "the backend genuinely has nothing". On an operator
 * cockpit that is the worst possible failure mode: "I couldn't ask" and
 * "the answer is zero" look identical. (Real example: every /assignments
 * endpoint 404s, and the page renders a clean, empty, entirely convincing grid.)
 *
 * Rather than patch `isError` into every page — 43 pages today, unbounded
 * tomorrow — failures are published here from the ONE global onError hook and
 * surfaced by `OperatorEventToasts`. Every current and future page is covered
 * automatically, and no page's render path changes.
 *
 * This is deliberately additive: today these errors go nowhere at all, so
 * publishing them cannot alter any existing behaviour.
 *
 * Pages that want inline, in-context error UI (see `positions/page.tsx`, which
 * renders `list.error` next to the table) should still do so — this is the
 * safety net for everything that doesn't, not a replacement for it.
 */

export interface QueryFailure {
  /** Stable identity for de-duplication — the serialised query key. */
  key: string;
  /** Short human label, e.g. "assignments". */
  subject: string;
  /** Error message shown under the subject. */
  detail: string;
}

type Listener = (f: QueryFailure) => void;

const listeners = new Set<Listener>();

/**
 * Per-key cooldown. Polling queries (some refetch every 5s) would otherwise
 * emit a toast per tick and bury the screen. One notification per key per
 * minute is enough to tell an operator "this data is not loading".
 */
const COOLDOWN_MS = 60_000;
const lastEmitted = new Map<string, number>();

export function subscribeQueryFailures(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function publishQueryFailure(f: QueryFailure): void {
  const now = Date.now();
  const prev = lastEmitted.get(f.key);
  if (prev !== undefined && now - prev < COOLDOWN_MS) {
    return;
  }
  lastEmitted.set(f.key, now);

  // Bound the cooldown map so a long session with many distinct keys cannot
  // grow it without limit.
  if (lastEmitted.size > 200) {
    for (const [k, t] of lastEmitted) {
      if (now - t >= COOLDOWN_MS) {
        lastEmitted.delete(k);
      }
    }
  }

  for (const fn of listeners) {
    try {
      fn(f);
    } catch {
      /* a broken listener must never break the query pipeline */
    }
  }
}

/** Test/HMR helper — drops cooldown state so emissions are deterministic. */
export function __resetQueryFailureBus(): void {
  lastEmitted.clear();
  listeners.clear();
}

/**
 * Derive a readable subject from a React Query key.
 * `['assignments', { limit: 500 }]` → "assignments".
 */
export function subjectFromQueryKey(queryKey: readonly unknown[]): string {
  const head = queryKey[0];
  return typeof head === 'string' && head.length > 0 ? head : 'data';
}
